'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { company } from '@/lib/company';
import { cn } from '@/lib/utils';
import { CharFadeLabel } from '@/components/effects/CharFadeLabel';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/products', label: 'Products' },
  { href: '/portfolio', label: 'Work' },
  { href: '/about-dilacode', label: 'About' },
  { href: '/company', label: 'Company' },
  { href: '/contact', label: 'Contact' },
];

/* ── Nav link with underline-slide hover (matching dilacode style) and a
      soft per-character fade ripple on load / hover ── */
function NavLink({ href, label, isDarkHeader, isActive, decodeDelay }: {
  href: string;
  label: string;
  isDarkHeader: boolean;
  isActive: boolean;
  decodeDelay: number;
}) {
  return (
    <Link
      href={href}
      className={cn(
        'group relative text-sm font-medium transition-opacity duration-300',
        isDarkHeader ? 'text-white' : 'text-black',
        isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
      )}
      style={{ fontFamily: 'var(--font-poppins), Poppins, sans-serif', fontSize: '14px', fontWeight: 500 }}
    >
      <CharFadeLabel text={label} mountDelay={decodeDelay} />
      {/* Underline slide-in from left on hover */}
      <span
        className="absolute -bottom-[5px] left-0 h-px w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100"
        style={{ background: isDarkHeader ? '#ffffff' : '#000000' }}
        aria-hidden="true"
      />
    </Link>
  );
}

export function Header() {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkHeader, setIsDarkHeader] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  // Handle scroll for header background
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Freeze the iOS safe-area top inset. In-browser Safari live-updates
  // env(safe-area-inset-top): correct at first paint, but when the browser
  // chrome minimizes on scroll the reported inset collapses while the page
  // stays edge-to-edge under the status bar — so an env()-padded header
  // slides up under the clock. Measure the inset with a probe element and
  // keep the largest value seen for the current orientation in
  // --dc-safe-top (see globals.css for the pre-hydration fallback).
  // Desktop and Android measure 0px, so nothing changes there.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const probe = document.createElement('div');
    probe.setAttribute('data-safe-area-probe', '');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;height:env(safe-area-inset-top, 0px)';
    document.body.appendChild(probe);

    let frozen = 0;
    let lastPortrait = window.innerHeight >= window.innerWidth;

    const measure = () => {
      const portrait = window.innerHeight >= window.innerWidth;
      const inset = probe.getBoundingClientRect().height;
      // Same orientation: only ever grow (ignores the scroll-collapsed 0).
      // Orientation flip: re-baseline (landscape top inset is genuinely 0).
      frozen = portrait === lastPortrait ? Math.max(frozen, inset) : inset;
      lastPortrait = portrait;
      document.documentElement.style.setProperty('--dc-safe-top', `${frozen}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      probe.remove();
      document.documentElement.style.removeProperty('--dc-safe-top');
    };
  }, []);

  // All pages start with dark header (white text/logo on dark bg).
  useEffect(() => {
    setIsDarkHeader(true);
  }, [pathname]);

  // Logo swap based on scroll (light-section triggers)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const lightSections = document.querySelectorAll('.light-section');

    const checkInitial = () => {
      document.querySelectorAll('.light-section').forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          setIsDarkHeader(false);
        }
      });
    };
    checkInitial();
    const timer = setTimeout(checkInitial, 100);

    // Track our own triggers — killing ScrollTrigger.getAll() would tear down
    // triggers owned by page components (portfolio pinning, skills fades).
    // GSAP is dynamic-imported so the Header (in every page's layout) doesn't
    // put it in every entry chunk (TASK-4).
    let cancelled = false;
    const triggers: Array<{ kill: () => void }> = [];
    if (lightSections.length > 0) {
      (async () => {
        const { ScrollTrigger } = await import('@/lib/gsap');
        if (cancelled) return;
        lightSections.forEach((section) => {
          triggers.push(
            ScrollTrigger.create({
              trigger: section,
              start: 'top 80px',
              end: 'bottom 80px',
              onEnter: () => setIsDarkHeader(false),
              onLeave: () => setIsDarkHeader(true),
              onEnterBack: () => setIsDarkHeader(false),
              onLeaveBack: () => setIsDarkHeader(true),
            }),
          );
        });
      })();
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
      triggers.forEach((t) => t.kill());
    };
  }, [pathname]);

  // Close menu on route change.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
  }, [isMenuOpen]);

  return (
    <>
      {/* ── Header — always transparent, no backdrop on scroll ── */}
      <header
        ref={headerRef}
        className="fixed left-0 right-0 top-0 z-[100] flex items-center justify-between transition-opacity duration-200 delay-200 ease-in-out"
        style={{
          paddingLeft: 'clamp(1.5rem, 8vw, 120px)',
          paddingRight: 'clamp(1.5rem, 8vw, 120px)',
          // Safe-area top: with viewport-fit=cover the header would otherwise
          // sit under the iPhone status bar / notch. --dc-safe-top is the
          // JS-frozen inset (see effect above) — raw env() collapses to 0
          // when Safari's chrome minimizes on scroll.
          paddingTop: 'var(--dc-safe-top, env(safe-area-inset-top, 0px))',
          height: 'calc(clamp(5rem, 8vw, 120px) + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
        }}
      >
        {/* Glass-blur frozen backdrop — fades in on scroll */}
        <div
          className={cn(
            'pointer-events-none absolute left-0 right-0 top-0 -z-10 transition-opacity duration-500',
            isScrolled && !isMenuOpen ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            // Grow by the safe-area inset so the blur also covers the
            // status-bar region the header now extends under.
            height: 'calc(120px + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            background: isDarkHeader
              ? 'linear-gradient(to bottom, rgba(23,56,53,0.85) 0%, rgba(23,56,53,0.4) 60%, rgba(23,56,53,0) 100%)'
              : 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0) 100%)',
          }}
        />
        {/* Wordmark — set in the site's own display face rather than a bitmap
            logo, so nothing here is an invented mark. The accent tick is the
            same motif the .eyebrow label uses site-wide. */}
        <Link
          href="/"
          className="relative z-[101] flex items-baseline gap-2"
          aria-label={`${company.name} — home`}
        >
          <span
            className="font-six-caps leading-none transition-colors duration-300"
            style={{
              fontSize: 'clamp(30px, 3.2vw, 40px)',
              letterSpacing: '0.06em',
              color: (isDarkHeader || isMenuOpen) ? '#ffffff' : '#000000',
            }}
          >
            {company.wordmark}
          </span>
          <span
            aria-hidden="true"
            className="inline-block rounded-full transition-colors duration-300"
            style={{
              width: '5px',
              height: '5px',
              marginBottom: '2px',
              backgroundColor: 'var(--color-accent)',
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block">
          <ul className="flex items-center gap-10">
            {navLinks.map((link, index) => (
              <li key={link.href}>
                <NavLink
                  href={link.href}
                  label={link.label}
                  isDarkHeader={isDarkHeader || isMenuOpen}
                  isActive={pathname === link.href}
                  decodeDelay={350 + index * 130}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="relative z-[101] flex min-h-11 min-w-11 flex-col items-center justify-center gap-1.5 p-2 lg:hidden"
          aria-label="Toggle menu"
        >
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white translate-y-2 rotate-45' : isDarkHeader ? 'bg-white' : 'bg-black')} />
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white opacity-0' : isDarkHeader ? 'bg-white' : 'bg-black')} />
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white -translate-y-2 -rotate-45' : isDarkHeader ? 'bg-white' : 'bg-black')} />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[98] flex flex-col lg:hidden"
          style={{ height: '100dvh', backgroundColor: '#0a0a0a' }}
        >
          {/* Nav links — left-aligned, editorial style */}
          <div
            className="flex flex-1 flex-col justify-center"
            style={{
              paddingLeft: '2rem',
              paddingRight: '2rem',
              // Clear the header AND the status bar (safe-area top inset).
              paddingTop: 'calc(5rem + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
            }}
          >
            <nav className="flex flex-col" style={{ gap: 0 }}>
              {navLinks.map((link, index) => {
                const isActive = pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="group block"
                    style={{
                      borderTop: index === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      paddingTop: '1.25rem',
                      paddingBottom: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      textDecoration: 'none',
                    }}
                  >
                    {/* Number index */}
                    <span
                      style={{
                        fontSize: '11px',
                        letterSpacing: '0.15em',
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.25)',
                        fontFamily: 'var(--font-poppins), Poppins, sans-serif',
                        fontWeight: 400,
                        minWidth: '2rem',
                        transition: 'color 0.3s',
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    {/* Active indicator line */}
                    <span
                      style={{
                        width: isActive ? '2rem' : '0',
                        height: '1px',
                        backgroundColor: '#ffffff',
                        transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        flexShrink: 0,
                      }}
                    />

                    {/* Link label */}
                    <span
                      style={{
                        fontFamily: 'var(--font-six-caps)',
                        fontSize: 'clamp(3rem, 12vw, 4.5rem)',
                        lineHeight: '0.95',
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.4)',
                        transition: 'color 0.3s, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      {/* Ripples on every menu open (overlay remounts) */}
                      <CharFadeLabel text={link.label} mountDelay={120 + index * 90} />
                    </span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Bottom — Contact info + Socials */}
          <div
            style={{
              paddingLeft: '2rem',
              paddingRight: '2rem',
              paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            }}
          >
            {/* Email */}
            <div style={{ marginBottom: '1.5rem' }}>
              <a
                href={`mailto:${company.email}`}
                style={{
                  fontSize: '13px',
                  letterSpacing: '0.1em',
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-poppins), Poppins, sans-serif',
                }}
              >
                {company.email}
              </a>
            </div>

            {/* Corporate identity */}
            <div className="flex items-center justify-end">
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {company.legalName}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
